import { useState } from 'react'
import { HttpVerb } from '../../../../../types/api-types'
import { FormDelegate, PageNavigation } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import { EditWellConstructionForm } from '../../../../components/well-forms/well-construction/well-head-construction-edit-form'
import EditRecordButtons from '../../../../components/wells/edit-record-buttons'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import { useWellId } from '../../../../hooks/use-well-id'
import { RecordFooter, useTitleChange } from '@msaf/core-react'

export function EditWellConstruction({ setIsDirty }: FormDelegate) {
  const wellId = useWellId()
  const {
    wellState: { isDraft, isGeothermalWell },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Construction`)

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${wellId}/construction/edit`,
    method: HttpVerb.PUT,
    saveAndContinueRoute: isGeothermalWell
      ? (id: string | number) => `/wells/${id}/edit/geothermal`
      : (id: string | number) => `/wells/${id}/edit/lithology`,
    cancelRoute: `/wells/${wellId}/view/construction`,
  }

  const stateQueryResult = useGetWellData(urls.requestUrl)
  const saveMutationResult = useSaveWellData(urls.requestUrl)

  const {
    fields,
    isLoading,
    isSaveError,
    isSaved,
    error,
    validationErrors,
    setValue,
    handleSave,
    addField,
    removeField,
    editField,
  } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  return (
    <>
      <EditWellConstructionForm
        isLoading={isLoading}
        fields={fields}
        setValue={setValue}
        addField={addField}
        removeField={removeField}
        validationErrors={validationErrors ?? []}
      />
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed
      >
        <EditRecordButtons handleSave={handleSave} urls={urls} />
      </RecordFooter>
    </>
  )
}

export default function EditWellConstructionWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellConstruction setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
